import { useEffect, useState } from 'react';
import { collection, getFirestore, onSnapshot, query, where } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../auth/firebase';
import { useSmsInbox } from './useSmsInbox';
import { formatBusinessNotificationText } from '../services/messageFormatting';

/**
 * How many unread messages the Inbox list shows — the number on the Inbox tab's badge. It counts exactly what
 * the list's rows count (each unread row's own badge), so the two always agree:
 *  - customer chat (Wix): your own count for each conversation (`unreadBy.<your uid>` in the wix-chat database; each
 *    staff member has their own, so opening a conversation never clears it for anyone else), for the conversations the
 *    list actually shows — not archived / merged, with a real preview, and not a business notice (email
 *    opened/clicked, new order/booking/invoice…), same rules as InboxConversationList's filter
 *  - texts (Twilio): your own unread texts, from the sms-conversations read markers (see useSmsInbox / markSmsRead)
 * Opening a conversation clears them (see ConversationThread).
 *
 * `enabled` keeps it at 0 (and the listener off) until there is a signed-in user to count for.
 */
export function useInboxUnreadCount(enabled: boolean): number {
  const { conversations: smsConversations } = useSmsInbox();
  const [chatUnread, setChatUnread] = useState(0);
  // On a phone Firebase restores its saved sign-in a moment after the app already shows a user, so
  // `auth.currentUser` can still be empty when this mounts — follow the auth state instead of reading it once
  const [uid, setUid] = useState<string | undefined>(auth.currentUser?.uid);
  useEffect(() => onAuthStateChanged(auth, (u) => setUid(u?.uid)), []);

  useEffect(() => {
    if (!enabled || !uid) return;
    const db = getFirestore(getApp(), 'wix-chat');
    return onSnapshot(
      query(collection(db, 'conversations'), where(`unreadBy.${uid}`, '>', 0)),
      (snap) => {
        let total = 0;
        for (const d of snap.docs) {
          const data = d.data();
          if (data._archived || data.mergedInto || data.archived) continue;
          // A conversation with an empty preview, or one that is only a business notice, is not in the list
          const preview = String(data.lastMessage ?? '').trim();
          if (!preview || formatBusinessNotificationText(preview) !== preview) continue;
          // The row's badge shows its count, or 1 when it is unread without one
          total += Number(data.unreadBy?.[uid]) || 1;
        }
        setChatUnread(total);
      },
      (e) => console.error('[useInboxUnreadCount] chat listener:', e),
    );
  }, [enabled, uid]);

  if (!enabled || !uid) return 0;
  const smsUnread = smsConversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
  return chatUnread + smsUnread;
}
